import { AfterViewInit, Directive, ElementRef, Inject, Input, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { EMPTY, Observable, Subject, catchError, combineLatest, delay, filter, fromEvent, map, switchMap, take, takeUntil, tap } from 'rxjs';

import { HotelResultData, ImageResultData } from '@hiptraveler/data-access/api';
import { ItineraryState } from '@hiptraveler/data-access/itinerary';
import { EventAction } from '@hiptraveler/data-access/event';
import { SearchResultDialogComponent } from '../search-result-dialog.component';
import { brandCampaignId, WINDOW } from '@hiptraveler/common';
import { DialogType } from './model';

@Directive({
  selector: '[searchResultAction]'
})
export class SearchResultActionDirective implements OnDestroy, AfterViewInit {

  @Input() image: ImageResultData;
  @Input() hotel: HotelResultData;
  @Input() dialogType: DialogType;

  subscription$ = new Subject<void>();

  constructor(
    @Inject(WINDOW) private window: any,
    private element: ElementRef<HTMLDivElement>,
    private dialogRef: MatDialogRef<SearchResultDialogComponent>,
    private store: Store
  ) { }

  ngOnDestroy(): void {
    this.subscription$.next();
  }

  ngAfterViewInit(): void {
    
    this.observeClickNavigation();

    combineLatest([
      this.store.select(ItineraryState.imageInformation),
      this.store.select(ItineraryState.hotelInformation)
    ]).pipe(
      delay(1000),
      takeUntil(this.subscription$)
    ).subscribe(this.observeClickNavigation.bind(this));
    
    this.store.select(ItineraryState.imageInformation).pipe(
      filter(Boolean), delay(1000), take(1),
      switchMap(() => this.clickNavigation([...this.getElementsByClass('tour-review--information-navigation')], '#reviews-wrapper')),
      takeUntil(this.subscription$)
    ).subscribe();
  }

  private observeClickNavigation(): void {

    this.clickNavigation([    
      ...this.getElementsByClass('details--title-open-in-new'),
      this.getElementByClass('price-offers--view-deal'),
      this.getElementByClass('result-actions--book-contact')
    ]).subscribe();
    
    this.clickNavigation([
      ...this.getElementsByClass('tour-info--check-availability')
    ], '#booking-assistant').subscribe();

    this.clickEvent([
      ...this.getElementsByClass('result-actions--edit')
    ]).subscribe(() => {
      this.dialogRef.close({
        type: 'edit-upload',
        id: this.image?.id || this.hotel?.id || ''
      });
    });
  }

  private getElementByClass(value: string): HTMLElement {
    return this.element.nativeElement.getElementsByClassName(value).item(0) as HTMLElement;
  }

  private getElementsByClass(value: string): HTMLElement[] {
    return this.element.nativeElement.querySelectorAll(`.${value}`) as any || [];
  }

  private clickEvent(elements: HTMLElement[]): Observable<Event> {
    return fromEvent(elements?.filter(e => !!e) || [], 'click').pipe(
      catchError(() => EMPTY),
      takeUntil(this.subscription$)
    )
  }

  private clickNavigation(elements: HTMLElement[], hash: string = ''): Observable<Event> {
    return fromEvent(elements?.filter(e => !!e) || [], 'click').pipe(
      catchError(() => EMPTY),
      tap(() => this.contentNavigation(hash)),
      takeUntil(this.subscription$)
    )
  }

  private contentNavigation(hash: string = ''): unknown {
    switch (this.dialogType) {
      case 'adventure': return this.adventureNavigation(hash);
      case 'hotel': return this.hotelNavigation();
      case 'food': return this.foodNavigation();
    }
  }

  private get tripId(): string {
    return this.store.selectSnapshot(ItineraryState.basicInfo)?.id || ''
  }

  private adventureNavigation(hash: string): void {
    this.store.dispatch(new EventAction.NavigateToExternalLinkEvent({
      objectId: this.image.id,
      actionId: this.tripId,
      actionType: 'checkavailability',
      objType: 'image'
    }));
   
    const url = `${this.image.imgSource || this.image.tourData?.tourUrl}${hash}`;
    const itemId = this.image.id || this.image.tourData?.imgId;
    const id = this.tripId || itemId;
    const updatedUrl = this.updateTourUrl(url, id);

    this.window.open(updatedUrl, '_blank');
  }
  
  private hotelNavigation(): void {
    this.store.dispatch(new EventAction.NavigateToExternalLinkEvent({
      objectId: this.hotel.id,
      actionId: this.tripId,
      actionType: 'checkavailability',
      objType: 'hotel'
    }));

    const url = `${this.hotel.lowestOffer?.deeplink || this.hotel.officialSite}`;
    const itemId = this.hotel.id;
    const updatedUrl = this.updateTourUrl(url, itemId);

    this.window.open(updatedUrl, '_blank');
  }

  private foodNavigation(): void {
    this.store.dispatch(new EventAction.NavigateToExternalLinkEvent({
      objectId: this.image.id,
      actionId: this.tripId,
      actionType: 'checkavailability',
      objType: 'image'
    }));

    const url = `${this.image?.taDataMap?.webUrl || this.image?.yelpData?.yelpUrl || this.image.tourData?.tourUrl || this.image.imgSource}`;
    const itemId = this.image.id;
    const updatedUrl = this.updateTourUrl(url, itemId);
    
    this.window.open(updatedUrl, '_blank');
  }

  private updateTourUrl(url: string, itemId?: string): string {
    const urlObj = this.getURLObject(url);
    const cId = brandCampaignId();

    if (!urlObj || !cId) {
      return url;
    }

    const updatedValue = `${cId}-${itemId}`;
    const parameters = ['campaign', 'utm_campaign', 'trackingId'];

    parameters.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, updatedValue);
      }
    });

    return urlObj.toString();
  }

  private getURLObject(url: string): URL | null {
    try {
      return new URL(url);
    } catch (e) {
      return null;
    }
  }
}
